import { Icon, Section } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import Modal from '@advanza/modal'
import React from 'react'
import PromosListContainer from '../../containers/PromosListContainer'
import TableList from '../TableList'
import Balloon from '../ui/Balloon'
import EligibleProvidersList from './EligibleProvidersList'
import PromoEntity from './PromoEntity'
import style from './PromosList.module.css'

class PromosList extends React.Component {
    constructor(props) {
        super(props)
        this.renderRow = this.renderRow.bind(this)
        this.copyToClipboard = this.copyToClipboard.bind(this)
        this.openEligibleProvidersModal = this.openEligibleProvidersModal.bind(this)
        this.closeEligibleProvidersModal = this.closeEligibleProvidersModal.bind(this)
        this.renderPromoCounts = this.renderPromoCounts.bind(this)
        this.fetchPromoStats = this.fetchPromoStats.bind(this)
        this.handleCheckAvailability = this.handleCheckAvailability.bind(this)
        this.state = {
            copiedMd5: null, // Track which md5 was copied
            eligibleProvidersModalOpen: false,
            selectedPromoId: null,
            selectedModalType: null, // 'eligible', 'pending', or 'active'
            promoStats: {}, // { promoId: { eligible, pending, active } }
            loadingPromoStats: {}, // { promoId: true/false } - track which promos are loading
        }
    }

    componentDidMount() {
        // Fetch services if not already loaded
        const { entities } = this.props
        const { services } = entities || {}
        if (!services || Object.keys(services).length === 0) {
            this.props.fetchServices && this.props.fetchServices('all')
        }

        // Fetch prospect labels for rules display
        if (this.props.fetchLabels) {
            this.props.fetchLabels({
                searchKey: 'serviceProviders',
                categories: ['serviceProviders'],
                noLimit: true,
            })
        }
    }

    fetchPromoStats(promoId) {
        if (!promoId) {
            return
        }

        // Check if promo has eligibility rules - skip if empty
        const { entities } = this.props
        const { promos } = entities || {}
        const promo = promos && promos[promoId]
        if (promo) {
            const eligibilityRules = promo.eligibility_rules
            // Skip if eligibility rules are empty or null
            if (
                !eligibilityRules ||
                (Array.isArray(eligibilityRules) && eligibilityRules.length === 0) ||
                (typeof eligibilityRules === 'object' && Object.keys(eligibilityRules).length === 0)
            ) {
                // Set stats to empty if no rules
                this.setState((prevState) => ({
                    promoStats: {
                        ...prevState.promoStats,
                        [promoId]: {
                            eligible: 0,
                            pending: 0,
                            active: 0,
                        },
                    },
                }))
                return
            }
        }

        // Set loading state
        this.setState((prevState) => ({
            loadingPromoStats: {
                ...prevState.loadingPromoStats,
                [promoId]: true,
            },
        }))

        call(`office/promos/get-promo-stats/${promoId}`)
            .then((response) => {
                const stats = response.data || response
                this.setState((prevState) => ({
                    promoStats: {
                        ...prevState.promoStats,
                        [promoId]: {
                            eligible: stats.eligible || 0,
                            pending: stats.pending || 0,
                            active: stats.active || 0,
                        },
                    },
                    loadingPromoStats: {
                        ...prevState.loadingPromoStats,
                        [promoId]: false,
                    },
                }))
            })
            .catch((error) => {
                console.error(`Failed to fetch stats for promo ${promoId}:`, error)
                this.setState((prevState) => ({
                    loadingPromoStats: {
                        ...prevState.loadingPromoStats,
                        [promoId]: false,
                    },
                }))
            })
    }

    handleCheckAvailability(promoId, e) {
        if (e) {
            e.stopPropagation()
        }
        this.fetchPromoStats(promoId)
    }

    renderPromoCounts(promoId) {
        const stats = this.state.promoStats[promoId]
        const isLoading = this.state.loadingPromoStats[promoId] === true

        // If loading, show loading text
        if (isLoading) {
            return (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>Loading...</span>
                </div>
            )
        }

        // Show counts if stats are available, otherwise show placeholders
        const { eligible = 0, pending = 0, active = 0 } = stats || {}

        return (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Balloon content="Number of providers that match the eligibility rules for this promo">
                    <span
                        style={{
                            color: '#1976d2',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                            this.openEligibleProvidersModal(promoId, 'eligible', e)
                        }}>
                        Eligible: {eligible}
                    </span>
                </Balloon>
                <Balloon content="Number of providers who have this promo but it has status pending">
                    <span
                        style={{
                            color: '#ff9800',
                            fontWeight: 'bold',
                            cursor: pending > 0 ? 'pointer' : 'help',
                            textDecoration: pending > 0 ? 'underline' : 'none',
                        }}
                        onClick={
                            pending > 0
                                ? (e) => {
                                      e.stopPropagation()
                                      this.openEligibleProvidersModal(promoId, 'pending', e)
                                  }
                                : undefined
                        }>
                        Pending: {pending}
                    </span>
                </Balloon>
                <Balloon content="Number of providers who have this promo with active status">
                    <span
                        style={{
                            color: '#4caf50',
                            fontWeight: 'bold',
                            cursor: active > 0 ? 'pointer' : 'help',
                            textDecoration: active > 0 ? 'underline' : 'none',
                        }}
                        onClick={
                            active > 0
                                ? (e) => {
                                      e.stopPropagation()
                                      this.openEligibleProvidersModal(promoId, 'active', e)
                                  }
                                : undefined
                        }>
                        Active: {active}
                    </span>
                </Balloon>
            </div>
        )
    }

    copyToClipboard(text, md5Key) {
        const copyPromise =
            navigator.clipboard && navigator.clipboard.writeText
                ? navigator.clipboard.writeText(text)
                : new Promise((resolve, reject) => {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea')
                      textArea.value = text
                      textArea.style.position = 'fixed'
                      textArea.style.opacity = '0'
                      document.body.appendChild(textArea)
                      textArea.select()
                      try {
                          document.execCommand('copy')
                          resolve()
                      } catch (err) {
                          reject(err)
                      }
                      document.body.removeChild(textArea)
                  })

        copyPromise
            .then(() => {
                // Set state to show checkmark
                this.setState({ copiedMd5: md5Key })
                // Reset after 1 second
                setTimeout(() => {
                    this.setState({ copiedMd5: null })
                }, 1000)
            })
            .catch((err) => {
                console.error('Failed to copy to clipboard:', err)
            })
    }

    openEligibleProvidersModal(promoId, modalType = 'eligible', e) {
        if (e) {
            e.stopPropagation()
        }
        this.setState({
            eligibleProvidersModalOpen: true,
            selectedPromoId: promoId,
            selectedModalType: modalType,
        })
    }

    closeEligibleProvidersModal(e) {
        // Prevent event propagation to avoid collapsing the promo entity
        if (e) {
            e.stopPropagation()
            e.preventDefault()
        }
        this.setState({
            eligibleProvidersModalOpen: false,
            selectedPromoId: null,
            selectedModalType: null,
        })
    }

    renderMd5Identifier(md5Identifier, promoId) {
        if (!md5Identifier) {
            return '-'
        }

        const firstChars = md5Identifier.substring(0, 4)
        const lastChars = md5Identifier.substring(md5Identifier.length - 4)
        const displayText = `${firstChars}...${lastChars}`
        const md5Key = `md5_${promoId}`
        const isCopied = this.state.copiedMd5 === md5Key

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Balloon content={md5Identifier}>
                    <small className="c-grey">{displayText}</small>
                </Balloon>
                {isCopied ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <Icon
                            small
                            style={{ cursor: 'pointer', marginLeft: '4px', color: 'green' }}>
                            check
                        </Icon>
                        <div
                            className="balloon animate-in"
                            style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '5px',
                                padding: '4px 8px',
                                backgroundColor: '#333',
                                color: '#fff',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                zIndex: 1000,
                            }}>
                            Copied to clipboard!
                        </div>
                    </div>
                ) : (
                    <Icon
                        small
                        style={{ cursor: 'pointer', marginLeft: '4px' }}
                        onClick={(e) => {
                            e.stopPropagation()
                            this.copyToClipboard(md5Identifier, md5Key)
                        }}>
                        content_copy
                    </Icon>
                )}
            </div>
        )
    }

    renderUserType(userType, specificProviderIds) {
        if (!userType || userType === '') {
            return <span>All</span>
        }

        let displayText = ''
        if (userType === 'basic') {
            displayText = 'Basic'
        } else if (userType === 'other_non_signed') {
            displayText = 'Other non signed'
        } else if (userType === 'all_not_signed') {
            displayText = 'All not signed'
        } else if (userType === 'churned') {
            displayText = 'Churned'
        } else if (userType === 'premium') {
            displayText = 'Premium'
        } else if (userType === 'specific') {
            if (specificProviderIds) {
                displayText = `Specific (IDs: ${specificProviderIds})`
            } else {
                displayText = 'Specific'
            }
        } else {
            displayText = userType
        }

        return <span>{displayText}</span>
    }

    renderOffers(offersInfo) {
        if (!offersInfo) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
        }

        // Handle array format only (new format)
        if (!Array.isArray(offersInfo)) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
        }

        if (offersInfo.length === 0) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
        }

        const formattedOffers = []

        offersInfo.forEach((offerObj) => {
            if (typeof offerObj === 'object' && offerObj !== null) {
                Object.keys(offerObj).forEach((offerType) => {
                    // Only handle new format: one-time-leads, recurring-leads, subscription-discount(-fixed-date)
                    if (
                        offerType !== 'one-time-leads' &&
                        offerType !== 'recurring-leads' &&
                        offerType !== 'subscription-discount' &&
                        offerType !== 'subscription-discount-fixed-date'
                    ) {
                        return // Skip old format offers
                    }

                    const offerData = offerObj[offerType]
                    if (typeof offerData === 'object' && offerData !== null) {
                        const value =
                            offerData.value !== undefined && offerData.value !== null
                                ? offerData.value
                                : ''
                        const type = offerData.type || ''
                        const promoLength =
                            offerData.promo_length !== undefined && offerData.promo_length !== null
                                ? offerData.promo_length
                                : null
                        const expiryDate = offerData.expiryDate || ''

                        // Format based on offer type (new format only)
                        let offerText = ''
                        if (offerType === 'subscription-discount-fixed-date') {
                            let valueDisplay = value
                            if (type === 'percentage') {
                                valueDisplay = `${value}%`
                            } else if (type === 'amount') {
                                valueDisplay = `€${value}`
                            }
                            if (offerData.endDate) {
                                const endDateObj = new Date(offerData.endDate)
                                offerText = `${valueDisplay} discount until ${endDateObj.toLocaleDateString()}`
                            } else {
                                offerText = `${valueDisplay} discount (no end date)`
                            }
                        } else if (offerType === 'subscription-discount') {
                            // Subscription discount
                            let valueDisplay = value
                            if (type === 'percentage') {
                                valueDisplay = `${value}%`
                            } else if (type === 'amount') {
                                valueDisplay = `€${value}`
                            }
                            if (promoLength !== null && promoLength !== undefined) {
                                if (promoLength === 0) {
                                    offerText = `${valueDisplay} discount (one-time)`
                                } else {
                                    offerText = `${valueDisplay} discount for ${promoLength} month${promoLength === 1 ? '' : 's'}`
                                }
                            } else {
                                offerText = `${valueDisplay} discount (ongoing)`
                            }
                        } else if (offerType === 'one-time-leads') {
                            // One-time free leads
                            let valueDisplay = value
                            if (type === 'percentage') {
                                valueDisplay = `${value}%`
                            } else if (type === 'amount') {
                                valueDisplay = `${value}`
                            }
                            if (expiryDate) {
                                const expiryDateObj = new Date(expiryDate)
                                const formattedDate = expiryDateObj.toLocaleDateString()
                                offerText = `${valueDisplay} one-time leads (expires: ${formattedDate})`
                            } else {
                                offerText = `${valueDisplay} one-time leads`
                            }
                        } else if (offerType === 'recurring-leads') {
                            // Recurring free leads
                            let valueDisplay = value
                            if (type === 'percentage') {
                                valueDisplay = `${value}%`
                            } else if (type === 'amount') {
                                valueDisplay = `${value}`
                            }
                            if (promoLength !== null && promoLength !== undefined) {
                                offerText = `${valueDisplay} recurring leads for ${promoLength} month${promoLength === 1 ? '' : 's'}`
                            } else {
                                offerText = `${valueDisplay} recurring leads (ongoing)`
                            }
                        }

                        if (offerText) {
                            formattedOffers.push(offerText)
                        }
                    }
                })
            }
        })

        if (formattedOffers.length === 0) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>No offers</span>
        }

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '12px',
                    width: '300px',
                    maxWidth: '300px',
                    overflow: 'hidden',
                }}>
                {formattedOffers.map((offerText, index) => (
                    <div
                        key={index}
                        style={{
                            padding: '2px 4px',
                            backgroundColor: '#e3f2fd',
                            borderRadius: '3px',
                            lineHeight: '1.3',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                        }}>
                        <span style={{ color: '#1976d2', overflow: 'hidden' }}>{offerText}</span>
                    </div>
                ))}
            </div>
        )
    }

    renderEligibilityRules(eligibilityRules, userType, specificProviderIds) {
        const { entities } = this.props
        const { services, labels } = entities || {}
        const servicesMap = services || {}
        const labelsMap = labels || {}

        const rulesToDisplay = []

        // Always add userType as the first rule
        let userTypeDisplay = 'All'
        if (userType && userType !== '') {
            if (userType === 'basic') {
                userTypeDisplay = 'Basic'
            } else if (userType === 'other_non_signed') {
                userTypeDisplay = 'Other non signed'
            } else if (userType === 'all_not_signed') {
                userTypeDisplay = 'All not signed'
            } else if (userType === 'churned') {
                userTypeDisplay = 'Churned'
            } else if (userType === 'premium') {
                userTypeDisplay = 'Premium'
            } else if (userType === 'specific') {
                if (specificProviderIds) {
                    userTypeDisplay = `Specific (IDs: ${specificProviderIds})`
                } else {
                    userTypeDisplay = 'Specific'
                }
            }
        }

        // Always add userType rule (shows "All" if empty/null)
        rulesToDisplay.push({
            key: 'userType',
            operator: '=',
            value: userTypeDisplay,
            isUserType: true,
        })

        // Add eligibility rules (excluding specificProviders)
        if (eligibilityRules && Array.isArray(eligibilityRules) && eligibilityRules.length > 0) {
            eligibilityRules.forEach((rule) => {
                if (rule && rule.key) {
                    // Hide specificProviders from the rules column
                    if (rule.key.toLowerCase() !== 'specificproviders') {
                        rulesToDisplay.push(rule)
                    }
                }
            })
        }

        if (rulesToDisplay.length === 0) {
            return <span style={{ color: '#999', fontStyle: 'italic' }}>No rules</span>
        }

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: '12px',
                    width: '300px',
                    maxWidth: '300px',
                    overflow: 'hidden',
                }}>
                {rulesToDisplay.map((rule, index) => {
                    if (!rule || !rule.key) {
                        return null
                    }

                    const { key, operator, value } = rule
                    let displayValue = value
                    let useColon = false

                    // For userType, services, and ProspectValue use colon instead of equals
                    if (
                        (key === 'userType' || key === 'services' || key === 'ProspectValue') &&
                        operator === '='
                    ) {
                        useColon = true
                    }

                    // Format the value for display
                    if (rule.isUserType) {
                        // userType is already formatted in the value
                        displayValue = value
                    } else if (key === 'ProspectValue') {
                        let labelIds = value
                        if (!Array.isArray(labelIds)) {
                            labelIds = labelIds ? [labelIds] : []
                        }
                        labelIds = labelIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
                        displayValue = labelIds
                            .map((id) => labelsMap[id]?.description ?? id)
                            .join(', ')
                    } else if (key === 'services') {
                        // Convert service IDs to service names
                        // Handle both array and string representation of array
                        let serviceIds = value
                        if (typeof value === 'string') {
                            try {
                                // Try to parse if it's a JSON string
                                serviceIds = JSON.parse(value)
                            } catch (e) {
                                // If not JSON, treat as single ID
                                serviceIds = [parseInt(value, 10)]
                            }
                        }
                        if (!Array.isArray(serviceIds)) {
                            serviceIds = [serviceIds]
                        }
                        // Convert to numbers and filter out invalid values
                        serviceIds = serviceIds
                            .map((id) => parseInt(id, 10))
                            .filter((id) => !isNaN(id))

                        displayValue = serviceIds
                            .map((serviceId) => {
                                const service = servicesMap[serviceId]
                                return service
                                    ? service.name_providers ||
                                          service.name ||
                                          `Service ${serviceId}`
                                    : `Service ${serviceId}`
                            })
                            .join(', ')
                    } else if (Array.isArray(value)) {
                        displayValue = value.join(', ')
                    } else if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value)
                    } else {
                        displayValue = String(value)
                    }

                    // Format operator for display
                    let operatorSymbol = operator
                    if (key === 'ProspectValue') {
                        operatorSymbol = operator === '!=' ? 'is not' : ':'
                    } else if (useColon) {
                        operatorSymbol = ':'
                    } else if (operator === '=') {
                        operatorSymbol = '='
                    } else if (operator === '!=') {
                        operatorSymbol = '≠'
                    } else if (operator === '>') {
                        operatorSymbol = '>'
                    } else if (operator === '>=') {
                        operatorSymbol = '≥'
                    } else if (operator === '<') {
                        operatorSymbol = '<'
                    } else if (operator === '<=') {
                        operatorSymbol = '≤'
                    }

                    // Format key for display
                    let keyDisplay = key.toLowerCase()
                    if (keyDisplay === 'usertype') {
                        keyDisplay = 'user'
                    } else if (keyDisplay === 'prospectvalue') {
                        keyDisplay = 'prospect value'
                    } else if (keyDisplay === 'premiumsigneddate') {
                        keyDisplay = 'premium signed date'
                    } else if (keyDisplay === 'specificproviders') {
                        keyDisplay = 'specific providers'
                    }

                    return (
                        <div
                            key={index}
                            style={{
                                padding: '2px 4px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '3px',
                                lineHeight: '1.3',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                wordBreak: 'break-word',
                            }}>
                            <span style={{ fontWeight: '500', overflow: 'hidden' }}>
                                {keyDisplay}
                            </span>{' '}
                            <span style={{ color: '#666', overflow: 'hidden' }}>
                                {operatorSymbol}
                            </span>{' '}
                            <span style={{ color: '#1976d2', overflow: 'hidden' }}>
                                {displayValue}
                            </span>
                        </div>
                    )
                })}
            </div>
        )
    }

    renderRow(id) {
        const { entities } = this.props
        const { promos } = entities
        const promo = promos[id]

        if (!promo) {
            return null
        }

        const isNew = typeof promo.promo_id === 'string'

        const expiryDisplay = promo.expiry_date
            ? `Available till ${new Date(promo.expiry_date).toLocaleDateString()}`
            : 'Available indefinitely'

        return {
            id,
            cols: [
                this.renderMd5Identifier(promo.md5_identifier, promo.promo_id || id),
                promo.name || '-',
                promo.identifier || '-',
                this.renderUserType(promo.user_type, promo.specific_provider_ids),
                expiryDisplay,
                this.renderEligibilityRules(
                    promo.eligibility_rules,
                    promo.user_type,
                    promo.specific_provider_ids,
                ),
                this.renderOffers(promo.offers_info),
            ],
            expandable: (
                <Section x y>
                    <PromoEntity entityId={id} />
                </Section>
            ),
            isLoading: promo._saving,
            expanded: isNew,
            expandOnClick: true,
            dontCloseOnClickOutside: true,
        }
    }

    definitions = {
        md5_identifier: { name: 'Promo Hash' },
        name: { name: 'Name' },
        identifier: { name: 'Identifier' },
        user_type: { name: 'User Type' },
        expiry_date: { name: 'Expiry Date' },
        eligibility_rules: { name: 'Rules' },
        offers_info: { name: 'Offer' },
    }

    render() {
        const { selectable, ...rest } = this.props
        const { eligibleProvidersModalOpen } = this.state

        return (
            <div className={style.promosListWrapper}>
                <TableList
                    {...rest}
                    selectable={selectable}
                    renderRow={this.renderRow}
                    definitions={this.definitions}
                />
                <Modal
                    open={eligibleProvidersModalOpen}
                    close={this.closeEligibleProvidersModal}
                    maxWidth="1400px">
                    <div
                        style={{ padding: '20px', maxHeight: '90vh', overflow: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}>
                        <h3>
                            {this.state.selectedModalType === 'eligible' && 'Eligible Providers'}
                            {this.state.selectedModalType === 'pending' &&
                                'Pending Promotion Providers'}
                            {this.state.selectedModalType === 'active' &&
                                'Active Promotion Providers'}
                        </h3>
                        <p style={{ marginBottom: '20px', color: '#666' }}>
                            {this.state.selectedModalType === 'eligible' &&
                                'Providers that match the eligibility rules for this promo'}
                            {this.state.selectedModalType === 'pending' &&
                                'Providers who have this promo with pending status'}
                            {this.state.selectedModalType === 'active' &&
                                'Providers who have this promo with active status'}
                        </p>
                        {eligibleProvidersModalOpen && this.state.selectedPromoId && (
                            <EligibleProvidersList
                                promoId={this.state.selectedPromoId}
                                modalType={this.state.selectedModalType}
                            />
                        )}
                    </div>
                </Modal>
            </div>
        )
    }
}

PromosList.propTypes = {}

export default PromosListContainer(PromosList)
